import { Route, Routes, NavLink, Link, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LogOut, LayoutDashboard, Users, Upload, Tags, Split, FileText, Search, MessageCircle, Inbox, Settings, KanbanSquare, Send, QrCode } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { ROLE_LABEL } from '../../../api/client';
import Dashboard from '../Dashboard';
import Usuarios from '../Usuarios';
import Leads from '../Leads';
import LeadDetail from '../LeadDetail';
import Importar from '../Importar';
import Ofertas from '../Ofertas';
import Captura from '../Captura';
import Propostas from '../Propostas';
import WhatsAppPage from '../WhatsAppPage';
import Prospeccao from '../Prospeccao';
import Pipeline from '../Pipeline';
import Capturados from '../Capturados';
import Atendimento from '../Atendimento';
import Campanhas from '../Campanhas';
import Configuracoes from '../Configuracoes';

type NavItem = { to: string; label: string; roles?: string[]; end?: boolean; icon: LucideIcon };

const NAV: NavItem[] = [
  { to: '/painel', label: 'Início', end: true, icon: LayoutDashboard },
  { to: '/painel/leads', label: 'Leads', icon: Users },
  { to: '/painel/pipeline', label: 'Pipeline', icon: KanbanSquare },
  { to: '/painel/capturados', label: 'Capturados', icon: Inbox },
  { to: '/painel/prospeccao', label: 'Prospecção', roles: ['admin', 'gestor'], icon: Search },
  { to: '/painel/importar', label: 'Importar', roles: ['admin', 'gestor'], icon: Upload },
  { to: '/painel/propostas', label: 'Propostas', icon: FileText },
  { to: '/painel/atendimento', label: 'Atendimento', icon: MessageCircle },
  { to: '/painel/campanhas', label: 'Campanhas', roles: ['admin', 'gestor'], icon: Send },
  { to: '/painel/whatsapp', label: 'Conectar WhatsApp', roles: ['admin', 'gestor'], icon: QrCode },
  { to: '/painel/ofertas', label: 'Ofertas', roles: ['admin', 'gestor'], icon: Tags },
  { to: '/painel/usuarios', label: 'Usuários', roles: ['admin'], icon: Users },
  { to: '/painel/configuracoes', label: 'Configurações', roles: ['admin'], icon: Settings },
];

export default function PainelApp() {
  const { user, logout } = useAuth();
  const [cfg, setCfg] = useState<{ company_legal_name: string; logo_path: string | null } | null>(null);

  useEffect(() => {
    fetch('/api/settings/public')
      .then((r) => r.json())
      .then((d) => setCfg((d as { settings: null }).settings ?? null))
      .catch(() => setCfg(null));
  }, []);
  const navigate = useNavigate();

  async function onLogout() {
    await logout();
    navigate('/painel/login');
  }

  return (
    <div className="min-h-screen bg-[var(--bg-subtle)]">
      <header className="flex h-14 items-center gap-3 border-b border-[var(--border)] bg-white px-4">
        {cfg?.logo_path ? (
            <img src={cfg.logo_path} alt="Logo" className="h-6 w-auto" />
          ) : null}
          <span className="text-sm font-bold tracking-tight">{cfg?.company_legal_name ?? 'Meta Telecom'}</span>
        <span className="text-xs text-[var(--text-dim)]">· Parceiro autorizado</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[var(--text-dim)]">
            {user?.fullName} · {ROLE_LABEL[user?.role ?? 'vendedor']}
          </span>
          <button
            onClick={onLogout}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-[var(--border-strong)] bg-white px-2 text-xs font-medium hover:bg-[var(--bg-subtle)] transition-colors"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>
      <div className="flex">
        <nav className="w-full overflow-x-auto border-b border-[var(--border)] bg-white px-3 py-2 lg:hidden">
          <div className="flex gap-1">
            {NAV.filter((n) => !n.roles || (user && n.roles.includes(user.role))).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  isActive
                    ? 'flex shrink-0 items-center gap-1.5 rounded-full bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white'
                    : 'flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--border-strong)] px-3 py-1.5 text-xs text-[var(--text-dim)] hover:bg-[var(--bg-subtle)]'
                }
              >
                <n.icon size={13} />
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <nav className="hidden w-48 shrink-0 border-r border-[var(--border)] bg-white p-3 lg:block">
          {NAV.filter((n) => !n.roles || (user && n.roles.includes(user.role))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                isActive
                  ? 'mb-1 flex items-center rounded-md bg-[var(--bg-subtle)] px-3 py-2 text-sm font-medium'
                  : 'mb-1 flex items-center rounded-md px-3 py-2 text-sm text-[var(--text-dim)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text)] transition-colors'
              }
            >
              <n.icon size={15} className="shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="importar" element={user?.role !== 'vendedor' ? <Importar /> : <Navigate to="/painel" replace />} />
            <Route path="captura" element={user?.role !== 'vendedor' ? <Captura /> : <Navigate to="/painel" replace />} />
            <Route path="ofertas" element={user?.role !== 'vendedor' ? <Ofertas /> : <Navigate to="/painel" replace />} />
            <Route path="propostas" element={<Propostas />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="capturados" element={<Capturados />} />
            <Route path="atendimento" element={user?.role !== 'vendedor' ? <WhatsAppPage /> : <Atendimento />} />
            <Route path="campanhas" element={user?.role !== 'vendedor' ? <Campanhas /> : <Navigate to="/painel" replace />} />
            <Route path="prospeccao" element={user?.role !== 'vendedor' ? <Prospeccao /> : <Navigate to="/painel" replace />} />
            <Route path="usuarios" element={user?.role === "admin" ? <Usuarios /> : <Navigate to="/painel" replace />} />
            <Route path="configuracoes" element={user?.role === "admin" ? <Configuracoes /> : <Navigate to="/painel" replace />} />
            <Route path="*" element={<Navigate to="/painel" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
